import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export interface CommitmentTimeParams {
  selectedCollectOption: 'pickup' | 'points' | 'delivery';
  obligatedDay?: string;
  obligatedDate: string;
  obligatedTime?: string;
  /** מספר ימים מההיום: 1=מחר, 2=מחרתיים. אם חסר – מחושב מ-obligatedDate */
  collectionDaysDiff?: number;
}

/**
 * שירות אחיד לתצוגת מועד התחייבות לאיסוף/משלוח.
 * מחזיר מחרוזת מגובשת לכל 4 העמודים: צ'אט, הזמנות שלי, ניהול הזמנות, סיכום הזמנה.
 */
@Injectable({ providedIn: 'root' })
export class CommitmentTimeDisplayService {
  constructor(private translate: TranslateService) {}

  /** מחשב הפרש ימים מהיום לפי obligatedDate בפורמט dd/MM/yyyy */
  computeCollectionDaysDiff(obligatedDate: string): number {
    if (!obligatedDate) return 0;
    const parts = obligatedDate.trim().split('/').map(Number);
    if (parts.length < 3) return 0;
    const [day, month, year] = parts;
    const today = new Date();
    const collectionDate = new Date(year, month - 1, day);
    today.setHours(0, 0, 0, 0);
    collectionDate.setHours(0, 0, 0, 0);
    const diffTime = collectionDate.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  /** מחזיר מחרוזת מלאה לתצוגת מועד התחייבות */
  getCommitmentTimeDisplayText(params: CommitmentTimeParams): string {
    const opt = params.selectedCollectOption || 'pickup';
    const date = (params.obligatedDate || '').trim();
    const day = (params.obligatedDay || '').trim();
    const time = (params.obligatedTime || '').trim();

    const diff = params.collectionDaysDiff ?? this.computeCollectionDaysDiff(date);
    const isRelativeDay = diff === 1 || diff === 2;

    const noLaterThan = this.translate.instant('collect.no-later-than');
    const timePart = time
      ? time === '00:00'
        ? this.translate.instant('collect.times.until-midnight')
        : `${this.translate.instant('collect.times.until')} ${time}`
      : '';
    const timeAndNoLater = timePart ? `${timePart} ${noLaterThan}` : noLaterThan;

    if (opt === 'delivery') {
      const base = this.translate.instant('collect.order-expected.delivery').replace(/\s*-\s*$/, '');
      if (isRelativeDay) {
        const rel = diff === 2
          ? this.translate.instant('collect.times.day-after-tomorrow')
          : this.translate.instant('collect.times.tomorrow');
        return `${base} ${rel} (${date}) ${timeAndNoLater}.`;
      }
      const atDay = this.translate.instant('collect.times.at-day');
      const suffix = timePart ? `${timePart}.` : `${noLaterThan}.`;
      return `${base} ${noLaterThan} ${atDay} ${day} (${date}) ${suffix}`;
    }

    if (opt === 'points') {
      const baseKey = isRelativeDay ? 'collect.order-expected.points-relative-day' : 'collect.order-expected.points';
      const base = this.translate.instant(baseKey);
      if (isRelativeDay) {
        const rel = diff === 2
          ? this.translate.instant('collect.times.day-after-tomorrow')
          : this.translate.instant('collect.times.tomorrow');
        return `${base} ${rel} (${date}) ${timeAndNoLater}.`;
      }
      const at = this.translate.instant('my-orders.at');
      return `${base} ${at} ${date} ${timeAndNoLater}.`;
    }

    // pickup
    const baseKey = isRelativeDay ? 'collect.order-expected.pickup-relative-day' : 'collect.order-expected.pickup';
    const base = this.translate.instant(baseKey);
    if (isRelativeDay) {
      const rel = diff === 2
        ? this.translate.instant('collect.times.day-after-tomorrow')
        : this.translate.instant('collect.times.tomorrow');
      return `${base} ${rel} (${date}) ${timeAndNoLater}.`;
    }
    const at = this.translate.instant('my-orders.at');
    return `${base} ${at} ${date} ${timeAndNoLater}.`;
  }
}
